import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, X, ArrowRight, HelpCircle, Info, Zap, Crown, Sparkles } from 'lucide-react';

const PLAN_ICONS = [Zap, Crown, Sparkles];

const CMMS_TIER_COLORS = [
  { bg: 'bg-slate-50', text: 'text-slate-800', sub: 'text-slate-500', btn: 'btn-outline', iconBg: 'bg-slate-200', iconText: 'text-slate-700' },
  { bg: 'bg-primary-600', text: 'text-white', sub: 'text-primary-100', btn: 'btn-primary', iconBg: 'bg-white/20', iconText: 'text-white' },
  { bg: 'bg-gray-800', text: 'text-white', sub: 'text-gray-300', btn: 'btn-outline', iconBg: 'bg-white/10', iconText: 'text-white' },
];
const CHATBOT_TIER_COLORS = [
  { bg: 'bg-purple-50', text: 'text-purple-900', sub: 'text-purple-500', btn: 'btn-outline', iconBg: 'bg-purple-200', iconText: 'text-purple-700' },
  { bg: 'bg-secondary-600', text: 'text-white', sub: 'text-secondary-100', btn: 'btn-secondary', iconBg: 'bg-white/20', iconText: 'text-white' },
  { bg: 'bg-gray-800', text: 'text-white', sub: 'text-gray-300', btn: 'btn-outline', iconBg: 'bg-white/10', iconText: 'text-white' },
];

const UNLIMITED = 999998;

const buildCmmsFeatures = (plan, t) => {
  const feats = [];
  if (plan.includedAssets != null)
    feats.push(plan.includedAssets >= UNLIMITED
      ? t('pricing.feat_unlimitedAssets')
      : t('pricing.feat_assets', { n: plan.includedAssets }));
  if (plan.includedUsers != null)
    feats.push(plan.includedUsers >= UNLIMITED
      ? t('pricing.feat_unlimitedUsers')
      : t('pricing.feat_users', { n: plan.includedUsers }));
  if (plan.includedLocations != null)
    feats.push(plan.includedLocations >= UNLIMITED
      ? t('pricing.feat_unlimitedLocations')
      : t('pricing.feat_locations', { n: plan.includedLocations }));
  if (plan.includedBranches != null)
    feats.push(plan.includedBranches >= UNLIMITED
      ? t('pricing.feat_unlimitedBranches')
      : t('pricing.feat_branches', { n: plan.includedBranches }));
  if (plan.features) {
    try { const extra = JSON.parse(plan.features); if (Array.isArray(extra)) feats.push(...extra.map(String)); } catch {}
  }
  return feats;
};

const buildChatbotFeatures = (plan, t) => {
  const feats = [];
  if (plan.messagesPerMonth != null) {
    if (plan.messagesPerMonth === -1) {
      feats.push(t('pricing.feat_unlimitedMsgs'));
    } else {
      const n = Number(plan.messagesPerMonth).toLocaleString();
      feats.push(t('pricing.feat_msgs', { n }));
    }
  }
  if (plan.maxChatbots != null) {
    if (plan.maxChatbots === -1) {
      feats.push(t('pricing.feat_unlimitedChatbots'));
    } else if (plan.maxChatbots === 1) {
      feats.push(t('pricing.feat_chatbot', { n: 1 }));
    } else {
      feats.push(t('pricing.feat_chatbots', { n: plan.maxChatbots }));
    }
  }
  if (plan.maxDocumentsPerChatbot != null) {
    if (plan.maxDocumentsPerChatbot === -1) {
      feats.push(t('pricing.feat_unlimitedDocs'));
    } else if (plan.maxDocumentsPerChatbot === 1) {
      feats.push(t('pricing.feat_doc', { n: 1 }));
    } else {
      feats.push(t('pricing.feat_docs', { n: plan.maxDocumentsPerChatbot }));
    }
  }
  if (plan.includedAgents != null && plan.includedAgents > 0)
    feats.push(plan.includedAgents === 1
      ? t('pricing.feat_agent', { n: 1 })
      : t('pricing.feat_agents', { n: plan.includedAgents }));
  if (plan.features) {
    try { const extra = JSON.parse(plan.features); if (Array.isArray(extra)) feats.push(...extra.map(String)); } catch {}
  }
  return feats;
};

const PricingPage = () => {
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState('annual');
  const [activeProduct, setActiveProduct] = useState('cmms'); // 'cmms' or 'chatbot'
  const [cmmsPlans, setCmmsPlans] = useState([]);
  const [chatbotPlans, setChatbotPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState(false);

  const transformCmmsPlan = (plan, idx, arr) => ({
    name: plan.planName || plan.name || 'Plan',
    monthly: parseFloat(plan.monthlyPrice) || 0,
    annual: parseFloat(plan.annualPrice) || 0,
    description: plan.description || '',
    popular: arr.length > 1 && idx === 1,
    features: buildCmmsFeatures(plan, t),
    disclaimer: t('pricing.extraSlots'),
  });

  const transformChatbotPlan = (plan, idx, arr) => ({
    name: plan.planName || plan.name || 'Plan',
    monthly: parseFloat(plan.monthlyPrice) || 0,
    annual: parseFloat(plan.annualPrice) || 0,
    description: plan.description || '',
    popular: arr.length > 1 && idx === 1,
    features: buildChatbotFeatures(plan, t),
    disclaimer: plan.overageEnabled && parseFloat(plan.pricePerAdditional1k) > 0
      ? t('pricing.extraMessagesRate', { price: plan.pricePerAdditional1k })
      : t('pricing.extraMessages'),
  });

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const [cmmsRes, chatbotRes] = await Promise.all([
          fetch('/api/subscriptions/cmms/plans'),
          fetch('/api/subscriptions/chatbot/plans'),
        ]);
        if (!cmmsRes.ok || !chatbotRes.ok) throw new Error('Failed to fetch plans');
        const cmmsData = await cmmsRes.json();
        const chatbotData = await chatbotRes.json();
        const cmmsArr = Array.isArray(cmmsData) ? cmmsData : (cmmsData.content ?? []);
        const chatbotArr = Array.isArray(chatbotData) ? chatbotData : (chatbotData.content ?? []);
        setCmmsPlans(cmmsArr.map((p, i, a) => transformCmmsPlan(p, i, a)));
        setChatbotPlans(chatbotArr.map((p, i, a) => transformChatbotPlan(p, i, a)));
      } catch (err) {
        console.error('Failed to fetch pricing plans:', err);
        setPlansError(true);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  const faqs = [
    { question: t('pricingPage.faq1Q'), answer: t('pricingPage.faq1A') },
    { question: t('pricingPage.faq2Q'), answer: t('pricingPage.faq2A') },
    { question: t('pricingPage.faq3Q'), answer: t('pricingPage.faq3A') },
    { question: t('pricingPage.faq4Q'), answer: t('pricingPage.faq4A') },
    { question: t('pricingPage.faq6Q'), answer: t('pricingPage.faq6A') },
  ];

  const plans = activeProduct === 'cmms' ? cmmsPlans : chatbotPlans;
  const tierColors = activeProduct === 'cmms' ? CMMS_TIER_COLORS : CHATBOT_TIER_COLORS;

  return (
    <>
      <Helmet>
        <title>Pricing - Choose Your Plan | mwbTechs</title>
        <meta
          name="description"
          content="Transparent pricing for CMMS and AI Chatbot. 14-day free trial, no credit card required. Plans starting from $49/month."
        />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        {/* Hero */}
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold text-gray-900 mb-4">
                {t('pricingPage.heroTitle1')} <span className="gradient-text">{t('pricingPage.heroTitle2')}</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                {t('pricingPage.heroSubtitle')}
              </p>

              {/* Product Toggle */}
              <div className="inline-flex items-center gap-4 bg-white rounded-full p-2 shadow-lg mb-8">
                <button
                  onClick={() => setActiveProduct('cmms')}
                  className={`px-6 py-3 rounded-full font-medium transition-all ${
                    activeProduct === 'cmms'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('pricingPage.cmmsPlans')}
                </button>
                <button
                  onClick={() => setActiveProduct('chatbot')}
                  className={`px-6 py-3 rounded-full font-medium transition-all ${
                    activeProduct === 'chatbot'
                      ? 'bg-secondary-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('pricingPage.chatbotPlans')}
                </button>
              </div>

              {/* Billing Toggle */}
              <div className="inline-flex items-center gap-4 bg-white rounded-full p-2 shadow-sm">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    billingCycle === 'monthly'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('pricingPage.monthly')}
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-6 py-2 rounded-full font-medium transition-all inline-flex items-center ${
                    billingCycle === 'annual'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('pricingPage.annual')}
                  <span className="ms-3 text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">
                    {t('pricingPage.save10')}
                  </span>
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="section">
          <div className="container-custom">
            {loadingPlans ? (
              <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {[1, 2, 3].map(i => (
                  <div key={i} className="pricing-card animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
                    <div className="h-10 bg-gray-200 rounded w-3/4 mb-6" />
                    <div className="space-y-3">
                      {[1, 2, 3, 4, 5].map(j => (
                        <div key={j} className="h-4 bg-gray-100 rounded w-full" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : plansError ? (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg">{t('pricingPage.contactPricing')}</p>
                <a href="mailto:sales@example.com" className="text-blue-600 underline mt-2 inline-block">
                  sales@example.com
                </a>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {plans.map((plan, index) => {
                  const colors = tierColors[index] ?? tierColors[0];
                  const Icon = PLAN_ICONS[index] ?? Sparkles;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className={`pricing-card overflow-hidden ${index === 1 ? 'pricing-card-featured' : ''}`}
                    >
                      {/* Colored header strip */}
                      <div className={`-mx-8 -mt-8 px-8 pt-8 pb-5 text-center mb-6 ${colors.bg}`}>
                        {index === 1 && (
                          <span className="inline-block text-xs font-semibold bg-white/25 text-white px-3 py-1 rounded-full mb-3 uppercase tracking-wider">
                            {t('pricingPage.mostPopular')}
                          </span>
                        )}
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 ${colors.iconBg}`}>
                          <Icon className={`w-6 h-6 ${colors.iconText}`} />
                        </div>
                        <h3 className={`text-2xl font-bold ${colors.text}`}>{plan.name}</h3>
                        {plan.description && (
                          <p className={`text-sm mt-1 ${colors.sub}`}>{plan.description}</p>
                        )}
                      </div>

                      {/* Price */}
                      <div className="text-center mb-6">
                        {plan.monthly === 0 ? (
                          <div className="flex items-baseline justify-center gap-2">
                            <span className="text-4xl font-bold text-gray-900">{t('pricingPage.custom')}</span>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-baseline justify-center gap-2">
                              <span className="text-5xl font-bold text-gray-900">
                                ${billingCycle === 'monthly' ? plan.monthly : Math.round(plan.annual / 12)}
                              </span>
                              <span className="text-gray-600">{t('pricingPage.perMonth')}</span>
                            </div>
                            {billingCycle === 'annual' && (
                              <p className="text-sm text-green-600 mt-2">${plan.annual} {t('pricingPage.billedAnnually')}</p>
                            )}
                          </>
                        )}
                      </div>

                      <ul className="space-y-3 mb-4">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {plan.disclaimer && (
                        <p className="flex items-start gap-1.5 text-xs text-gray-500 italic mb-6">
                          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                          {plan.disclaimer}
                        </p>
                      )}

                      <Link
                        to="/signup"
                        className={`w-full btn ${colors.btn}`}
                      >
                        {t('pricingPage.startFreeTrial')}
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* FAQ */}
        <section className="section bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('pricingPage.faqTitle')}
              </h2>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl p-6 shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-6 h-6 text-primary-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-sm bg-gradient-primary text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-4">{t('pricingPage.ctaTitle')}</h2>
            <p className="text-xl text-white opacity-90 mb-8">
              {t('pricingPage.ctaSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/signup" className="btn btn-white text-lg">
                {t('pricingPage.startFreeTrial')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/contact" className="btn btn-outline text-white border-white hover:bg-white hover:text-primary-600 text-lg">
                {t('pricingPage.contactSales')}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PricingPage;
